/**
 * Tiroir « Infos du niveau » : briefing (description, DA, bestiaire) et aide de jeu.
 *
 * Le briefing occupait 136 px au-dessus du plateau, en permanence. Il vit maintenant dans un
 * tiroir ouvert à la demande depuis la barre du haut : le plateau récupère toute la hauteur, comme
 * dans un jeu, et l'information reste à un geste. Même mécanique que le panneau « Effets de la
 * run » (U-03) : voile cliquable, Échap pour fermer, focus sur « Fermer » à l'ouverture, et aucune
 * pause de la vague (le tiroir n'est pas modal).
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { BriefingNiveau } from './BriefingNiveau';

import type { Niveau } from '@elemora/shared/content';

export interface ProprietesPanneauInfosNiveau {
  readonly niveau: Niveau;
  /** Titre complet du niveau (« Niveau 1 — L'Éveil du Brasier »), déjà traduit. */
  readonly titre: string;
  readonly onFermer: () => void;
}

export function PanneauInfosNiveau({ niveau, titre, onFermer }: ProprietesPanneauInfosNiveau) {
  const { t } = useTranslation();
  const boutonFermer = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    boutonFermer.current?.focus();
  }, []);

  useEffect(() => {
    const surTouche = (evenement: KeyboardEvent): void => {
      if (evenement.key === 'Escape') {
        evenement.stopPropagation();
        onFermer();
      }
    };
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('keydown', surTouche);
    };
  }, [onFermer]);

  return (
    <div className="panneau-effets panneau-infos" data-panneau-infos="ouvert">
      <div className="panneau-effets__voile" aria-hidden="true" onClick={onFermer} />
      <div
        className="panneau-effets__tiroir"
        role="dialog"
        aria-modal="false"
        aria-labelledby="titre-infos-niveau"
      >
        <header className="panneau-effets__entete">
          <div className="panneau-effets__titres">
            <h2 className="panneau-effets__titre" id="titre-infos-niveau">
              {t('jeu.infos.titre')}
            </h2>
            <p className="panneau-effets__sousTitre">{titre}</p>
          </div>
          <button
            type="button"
            ref={boutonFermer}
            className="bouton bouton--secondaire panneau-effets__fermer"
            data-fermer-infos="oui"
            onClick={onFermer}
          >
            {t('jeu.infos.fermer')}
          </button>
        </header>
        <div className="panneau-effets__corps">
          <BriefingNiveau niveau={niveau} idTitre="briefing-titre-infos" />
          <section className="effets-section" aria-labelledby="titre-aide-jeu">
            <h3 className="effets-section__titre" id="titre-aide-jeu">
              {t('jeu.aideTitre')}
            </h3>
            <ul className="aide-ecran__liste">
              <li>{t('jeu.aide1')}</li>
              <li>{t('jeu.aide2')}</li>
              <li>{t('jeu.aide3')}</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
